import { Injectable } from "@angular/core";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from '@datadog/browser-logs';
import { environment } from "../../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class DatadogService {
  private initialized = false;
  private currentUserId: string | null = null;

  /**
   * Inicializa o Datadog RUM e seta o contexto do streamer.
   */
  init(streamerId?: string): void {
    const cfg = environment.datadog;
    if (!cfg?.enabled || !cfg.clientToken || !cfg.applicationId) {
      return;
    }

    if (streamerId) {
      this.currentUserId = streamerId;
    }

    if (!this.initialized) {
      datadogRum.init({
        applicationId: cfg.applicationId,
        clientToken: cfg.clientToken,
        site: cfg.site,
        service: cfg.service,
        env: cfg.env,
        version: cfg.version,
        sessionSampleRate: cfg.sessionSampleRate ?? 100,
        sessionReplaySampleRate: cfg.sessionReplaySampleRate ?? 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
        silentMultipleInit: true,
      });
      datadogLogs.init({
        clientToken: cfg.clientToken,
        site: cfg.site,
        forwardErrorsToLogs: true,
        sessionSampleRate: cfg.sessionSampleRate ?? 100
      });
      this.initialized = true;
    }

    this.applyUser();
  }

  logInfo(message: string, context?: Record<string, any>): void {
    datadogLogs.logger.info(message, context);
  }

  logWarn(message: string, context?: Record<string, any>): void {
    datadogLogs.logger.warn(message, context);
  }

  logError(message: string, context?: Record<string, any>): void {
    datadogLogs.logger.error(message, context);
  }

  /**
   * Ajusta o nome da view no Datadog RUM (ex.: donations/<streamerId>).
   */
  setViewName(name: string): void {
    const cfg = environment.datadog;
    if (!cfg?.enabled || !cfg.clientToken || !cfg.applicationId) {
      return;
    }
    this.init();
    try {
      datadogRum.startView({ name });
    } catch {
      // ignore
    }
  }

  private applyUser(): void {
    if (!this.currentUserId) return;
    try {
      datadogRum.setUser({
        id: this.currentUserId,
        type: "streamer",
      });
    } catch {
      // ignore
    }
  }

  /**
   * Envia uma ação customizada para o Datadog RUM (telemetria estruturada).
   */
  trackAction(name: string, context?: Record<string, any>): void {
    const cfg = environment.datadog;
    if (!cfg?.enabled || !cfg.clientToken || !cfg.applicationId) {
      return;
    }
    this.init();
    try {
      datadogRum.addAction(name, {
        streamerId: this.currentUserId,
        ...(context || {}),
      });
    } catch {
      // ignore
    }
  }
}
